<template>
  <div class="advanced-table-creator" @click="closeDropdown">
    <div class="table-container">
      <div class="table-wrapper">
        <div class="table-positioning">
          <!-- Column Insert Indicators - positioned above table -->
          <div class="column-indicators">
            <div
              v-for="(cell, cellIndex) in rows[0]?.cells"
              :key="`col-indicator-${cell.id}`"
              class="column-indicator-wrapper"
              :style="{ left: getColumnPosition(cellIndex) }"
            >
              <!-- Column insert indicator between columns -->
              <div
                v-if="cellIndex < rows[0].cells.length - 1 && canEditSchema"
                class="column-insert-indicator"
              >
                <div class="indicator-dot"></div>
                <button
                  @click="addColumn(cellIndex)"
                  class="insert-btn column-btn"
                  title="Insert column"
                >
                  +
                </button>
              </div>

              <!-- Column insert indicator for the last column -->
              <div
                v-if="cellIndex === rows[0].cells.length - 1 && canEditSchema"
                class="column-insert-indicator end-column-indicator"
              >
                <div class="indicator-dot"></div>
                <button
                  @click="addColumn(cellIndex)"
                  class="insert-btn column-btn"
                  title="Insert column"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <!-- Row Insert Indicators - positioned to the left of table -->
          <div class="row-indicators">
            <div
              v-for="(row, rowIndex) in rows"
              :key="`row-indicator-${row.id}`"
              class="row-indicator-wrapper"
              :style="{ top: getRowPosition(rowIndex) }"
            >
              <!-- Row insert indicator between rows -->
              <div
                v-if="rowIndex < rows.length - 1 && !editChecklist && mode !== 'VIEW'"
                class="row-insert-indicator"
              >
                <div class="indicator-dot"></div>
                <button
                  @click="addRow(rowIndex)"
                  class="insert-btn row-btn"
                  title="Insert row"
                >
                  +
                </button>
              </div>

              <!-- Row insert indicator for the last row -->
              <div
                v-if="rowIndex === rows.length - 1 && !editChecklist && mode !== 'VIEW'"
                class="row-insert-indicator end-row-indicator"
              >
                <div class="indicator-dot"></div>
                <button
                  @click="addRow(rowIndex)"
                  class="insert-btn row-btn"
                  title="Insert row"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <!-- Main Table -->
          <table ref="mainTable" class="main-table">
            <tbody>
              <tr
                v-for="(row, rowIndex) in rows"
                :key="row.id"
                class="table-row"
                :class="{ 'header-row': rowIndex === 0 }"
              >
                <!-- Row Cells -->
                <td
                  v-for="(cell, cellIndex) in row.cells"
                  :key="cell.id"
                  class="table-cell"
                >
                  <div class="cell-content" :class="{ 'header-cell': rowIndex === 0 }">
                    <!-- Header Cell (Row 0) -->
                    <template v-if="rowIndex === 0">
                      <!-- Data Type Icon -->
                      <span class="data-type-icon-wrapper" :class="'type-' + (columnDataTypes[cellIndex] || 'TEXT').toLowerCase()">
                        <img
                          :src="getDataTypeIcon(columnDataTypes[cellIndex])"
                          class="data-type-icon"
                          alt="Data type icon"
                        />
                      </span>

                      <!-- Column Name Label & Required Asterisk (Checklist in use / Form mode) -->
                      <div
                        v-if="!canEditSchema"
                        class="header-column-label"
                        v-tooltip="cell.content || `Column ${cellIndex + 1}`"
                      >
                        <span class="header-label-text">{{ cell.content || `Column ${cellIndex + 1}` }}</span>
                        <span
                          v-if="columnRequired[cellIndex]"
                          class="column-required-asterisk"
                          v-tooltip="'Mandatory field'"
                        >*</span>
                      </div>

                      <!-- Column Name Input (Schema Edit Mode) -->
                      <input
                        v-else
                        v-model="cell.content"
                        type="text"
                        :placeholder="activeHeaderCellId === cell.id ? '' : `Column ${cellIndex + 1}`"
                        class="cell-input header-input"
                        v-tooltip="cell.content || (activeHeaderCellId === cell.id ? '' : `Column ${cellIndex + 1}`)"
                        @input="onDataChange"
                        @focus="activeHeaderCellId = cell.id"
                        @blur="handleHeaderBlur(cell, cellIndex)"
                      />

                      <!-- Required Indicator / Toggle in Header (Schema Edit Mode) -->
                      <div
                        v-if="canEditSchema"
                        class="header-required-toggle"
                        :class="{ 'is-checked': columnRequired[cellIndex] }"
                        v-tooltip="columnRequired[cellIndex] ? 'Mandatory column (Toggle to make optional)' : 'Optional column (Toggle to make mandatory)'"
                        role="button"
                        :aria-label="columnRequired[cellIndex] ? 'Required column' : 'Optional column'"
                        @click.stop="toggleColumnRequired(cellIndex)"
                      >
                        <div class="header-toggle-track">
                          <div class="header-toggle-thumb"></div>
                        </div>
                      </div>

                      <!-- Custom List Name Badge (inline for CONFIGURATION_LIST) -->
                      <span
                        v-if="columnDataTypes[cellIndex] === 'CONFIGURATION_LIST' && columnParentCustomList[cellIndex]"
                        :ref="`parentPickerBadge-${cellIndex}`"
                        class="parent-list-badge"
                        :class="{ 'is-clickable': canEditSchema }"
                        v-tooltip="canEditSchema ? `Custom List: ${columnParentCustomList[cellIndex].name} (Click to change)` : columnParentCustomList[cellIndex].name"
                        @click.stop="openParentPicker(cellIndex)"
                      >
                        <span class="badge-text">{{ columnParentCustomList[cellIndex].name }}</span>
                        <svg v-if="canEditSchema" class="badge-chevron" viewBox="0 0 10 6" aria-hidden="true">
                          <path d="M0 0l5 5 5-5z" fill="currentColor" />
                        </svg>
                      </span>
                      <span
                        v-else-if="columnDataTypes[cellIndex] === 'CONFIGURATION_LIST' && canEditSchema"
                        :ref="`parentPickerBadge-${cellIndex}`"
                        class="parent-list-badge is-placeholder is-clickable"
                        v-tooltip="'Select custom list'"
                        @click.stop="openParentPicker(cellIndex)"
                      >
                        <span class="badge-text">+ Select</span>
                        <svg class="badge-chevron" viewBox="0 0 10 6" aria-hidden="true">
                          <path d="M0 0l5 5 5-5z" fill="currentColor" />
                        </svg>
                      </span>

                      <!-- Data Type Dropdown Button & Menus -->
                      <div v-if="canEditSchema" class="data-type-dropdown">
                        <button
                          :ref="`dataTypeBtn-${cellIndex}`"
                          @click.stop="toggleDataTypeDropdown(cellIndex)"
                          class="data-type-btn"
                          :title="`Data Type: ${getDataTypeLabel(columnDataTypes[cellIndex])}`"
                        >
                          <svg class="dropdown-triangle" viewBox="0 0 10 6" aria-hidden="true">
                            <path d="M0 0l5 5 5-5z" fill="currentColor" />
                          </svg>
                        </button>

                        <!-- Dropdown Menu -->
                        <div
                          v-if="showDropdownForColumn === cellIndex"
                          class="dropdown-menu"
                          :style="dataTypeDropdownStyle"
                          @click.stop
                        >
                          <div
                            v-for="option in dataTypeOptions"
                            :key="option.id"
                            @click="selectDataType(cellIndex, option.key)"
                            class="dropdown-item s"
                            :class="{ 'selected': columnDataTypes[cellIndex] === option.key }"
                          >
                            <span class="option-icon-wrapper" :class="'type-' + (option.key || 'TEXT').toLowerCase()">
                              <img :src="option.icon" alt="icon" class="option-icon" />
                            </span>
                            <span class="option-label">{{ option.caption }}</span>
                          </div>

                          <!-- Partition line -->
                          <!-- <div class="dropdown-partition"></div> -->

                          <!-- Required Toggle Option -->
                          <!-- <div
                            @click="toggleColumnRequired(cellIndex)"
                            class="dropdown-item required-toggle-item s"
                            :class="{ 'is-active': columnRequired[cellIndex] }"
                          >
                            <span class="required-asterisk-icon">*</span>
                            <span class="option-label">Mandatory Field</span>
                            <div class="mini-toggle" :class="{ 'is-checked': columnRequired[cellIndex] }">
                              <div class="mini-toggle-slider"></div>
                            </div>
                          </div> -->

                          <!-- Partition line -->
                          <div class="dropdown-partition"></div>

                          <!-- Delete option -->
                          <div
                            @click="deleteColumn(cellIndex)"
                            class="dropdown-item delete-option s"
                          >
                            <span class="option-icon-wrapper type-delete">
                              <img src="~@/assets/images/trash-2.svg" alt="" class="option-icon">
                            </span>
                            <span class="option-label">Delete Column</span>
                          </div>
                        </div>

                        <div
                          v-if="showParentPickerForColumn === cellIndex && canEditSchema"
                          class="dropdown-menu"
                          :style="parentPickerDropdownStyle"
                          @click.stop
                        >
                          <div class="dropdown-search">
                            <input
                              type="text"
                              placeholder="Search list..."
                              :value="parentListSearch[cellIndex] || ''"
                              @input="$set(parentListSearch, cellIndex, $event.target.value)"
                              @click.stop
                              class="dropdown-search-input"
                            />
                          </div>
                          <div
                            v-for="list in filteredParentLists(cellIndex)"
                            :key="list.id"
                            @click="selectParentCustomList(cellIndex, list)"
                            class="dropdown-item s"
                            :class="{ selected: columnParentCustomList[cellIndex]?.id === list.id }"
                          >
                            <span class="option-label">{{ list.name }}</span>
                          </div>

                          <div v-if="filteredParentLists(cellIndex).length === 0" class="dropdown-item s">
                            <span class="option-label" style="color: #aaa;">No lists found</span>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Data Row Inputs (type based on column data type) -->
                    <template v-else>
                      <!-- Text Input -->
                      <input
                        v-if="columnDataTypes[cellIndex] === 'TEXT'"
                        v-model="cell.content"
                        :disabled="isDataCellDisabled(rowIndex, cellIndex)"
                        type="text"
                        class="cell-input"
                        @input="onDataChange"
                      />

                      <!-- Number Input -->
                      <input
                        v-else-if="columnDataTypes[cellIndex] === 'NUMBER'"
                        v-model.number="cell.content"
                        :disabled="isDataCellDisabled(rowIndex, cellIndex)"
                        type="number"
                        class="cell-input"
                        @input="onDataChange"
                      />

                      <!-- Boolean Checkbox -->
                      <div
                        v-else-if="columnDataTypes[cellIndex] === 'BOOLEAN'"
                        class="checkbox-wrapper"
                      >
                        <input
                          v-model="cell.content"
                          type="checkbox"
                          class="cell-checkbox"
                          :disabled="isBooleanCellDisabled(rowIndex, cellIndex)"
                          @change="onDataChange"
                        />
                      </div>

                      <!-- Date Input -->
                      <input
                        v-else-if="columnDataTypes[cellIndex] === 'DATE'"
                        v-model="cell.content"
                        type="date"
                        class="cell-input"
                        :disabled="isDataCellDisabled(rowIndex, cellIndex)"
                        @input="onDataChange"
                      />

                      <!-- CONFIGURATION_LIST — tag input with custom list picker -->
                      <div
                        v-else-if="columnDataTypes[cellIndex] === 'CONFIGURATION_LIST'"
                        class="config-list-cell"
                        :class="{ 'is-dropdown-open': isConfigCellDropdownOpen(rowIndex, cellIndex) }"
                        @click.stop
                      >
                        <template v-if="(formTemplate || mode !== 'TEMPLATE') && getParentCustomListId(cellIndex)">
                          <custom-input
                            :ref="`configInput-${rowIndex}-${cellIndex}`"
                            :customListId="getParentCustomListId(cellIndex)"
                            :tagGroupData="getHydratedCellTagGroupData(rowIndex, cellIndex)"
                            :timeLineEdit="isConfigCellReadOnly(rowIndex, cellIndex)"
                            @addNewTag="onConfigTagAdded(rowIndex, cellIndex, $event)"
                            @resetSelectedLine="onConfigTagReset(rowIndex, cellIndex)"
                            @dropdown-state-change="onConfigDropdownStateChange(rowIndex, cellIndex, $event)"
                          />
                        </template>
                        <span v-else class="parent-list-badge">--</span>
                      </div>

                      <input
                        v-else
                        v-model="cell.content"
                        type="text"
                        class="cell-input"
                        :disabled="isDataCellDisabled(rowIndex, cellIndex)"
                        @input="onDataChange"
                      />
                    </template>
                  </div>
                </td>

                <!-- Delete Row Button -->
                <td v-if="!editChecklist && mode !== 'VIEW'" class="row-delete-cell">
                  <button
                    v-if="rows.length > 1 && rowIndex > 0"
                    @click="deleteRow(rowIndex)"
                    class="delete-btn row-delete-btn"
                    title="Delete row"
                  >
                    <img src="~@/assets/images/trash-2.svg" alt="">
                  </button>
                </td>
              </tr>

              <!-- Add Row at End Button -->
              <tr class="add-row-row" v-if="!editChecklist && mode !== 'VIEW'">
                <td :colspan="columnCount + 1" class="add-row-cell">
                  <button @click="addRow()" class="add-row-end-btn">
                    <span class="icon">+</span>
                    Add Row at End
                  </button>
                </td>
              </tr>

            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Table Info (removed Save Table button) -->
    <!-- <div class="table-info"> -->
      <!-- <span class="table-stats">
        {{ rows.length }} row{{ rows.length !== 1 ? 's' : '' }} × {{ columnCount }}
        column{{ columnCount !== 1 ? 's' : '' }}
      </span> -->
    <!-- </div> -->
  </div>
</template>

<script>
import { iconMap } from '@/utils/formFieldIcons'
import CustomInput from '@/components/common/tagAndConfigListInput.vue'
import { GetActiveCustomLists } from '@/api'
export default {
  name: 'AdvancedTableCreator',
  components: { CustomInput },

  props: {
    initialRows: {
      type: Array,
      default: null
    },
    initialColumnDataTypes: {
      type: Array,
      default: null
    },
    initialColumnRequired: {
      type: Array,
      default: null
    },
    initialColumnParentCustomList: {
      type: Object,
      default: () => ({})
    },
    editChecklist: {
      type: Boolean,
      default: false
    },
    customFormFields: {
      type: Array,
      default: () => ([])
    },
    formTemplate: {
      type: Boolean,
      default: false
    },
    isFormMode: {
      type: Boolean,
      default: false
    },
    formColumnDefinitions: {
      type: Array,
      default: () => ([])
    },
    previewMode: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'CHECKLIST'
    },
    data: { type: Object, default: () => ({}) }

  },
  computed: {
    canEditSchema () {
      return !this.isFormMode && !this.editChecklist && (this.mode === 'TEMPLATE' || this.mode === 'CHECKLIST')
    },
    shouldRespectColumnEditability () {
      return this.isFormMode && this.formColumnDefinitions.length > 0
    }
  },
  data () {
    return {
      rows: [],
      columnCount: 3,
      columnDataTypes: [],
      columnRequired: [],
      dataTypeOptions: [],
      showDropdownForColumn: null,
      showParentPickerForColumn: null,
      dataTypeDropdownStyle: {},
      parentPickerDropdownStyle: {},
      columnPositions: [],
      rowPositions: [],
      resizeObserver: null,
      recalculateRafId: null,
      cellTagTries: {},
      cellTagGroupData: {},
      cellLastParentIds: {},
      autoOpenColumnIndex: null,
      columnParentCustomList: {},
      parentCustomLists: [],
      parentListSearch: {},
      cellTagData: {},
      openConfigDropdownKey: null,
      addedRowIds: [],
      activeHeaderCellId: null
    }
  },
  watch: {
    initialRows: {
      immediate: true,
      handler (val) {
        if (!val || !val.length) return
        this.rows = JSON.parse(JSON.stringify(val))
        this.addedRowIds = []
        this.columnCount = this.rows[0]?.cells.length || 3
        if (this.initialColumnDataTypes?.length) {
          this.columnDataTypes = [...this.initialColumnDataTypes]
        } else {
          this.columnDataTypes = Array.from({ length: this.columnCount }, () => 'TEXT')
        }
        if (this.initialColumnRequired?.length) {
          this.columnRequired = [...this.initialColumnRequired]
        } else {
          this.columnRequired = this.extractColumnRequired()
        }
        this.columnParentCustomList = { ...this.initialColumnParentCustomList }
        const headerRow = this.rows[1]
        if (headerRow && !Object.keys(this.columnParentCustomList).length) {
          headerRow.cells.forEach((cell, cellIndex) => {
            if (
              Array.isArray(cell.content) &&
              cell.content.length &&
              cell.content[0]?.custom_list_id
            ) {
              this.$set(this.columnParentCustomList, cellIndex, {
                id: cell.content[0].custom_list_id,
                name: cell.content[0].custom_list_name || ''
              })
            }
          })
        }

        this.$nextTick(() => {
          this.restoreCellTagData()
          this.restoreParentCustomListFromData()
          this.restoreParentCustomListFromColumns()
          this.restoreColumnRequired()
          this.recalculatePositions()
        })
      }
    },
    customFormFields: {
      immediate: true,
      handler () {
        this.initializeDataTypeOptions()
      }
    },
    formColumnDefinitions: {
      immediate: true,
      handler (val) {
        if (Array.isArray(val) && val.length) {
          this.restoreColumnRequired()
        }
      }
    },
    data: {
      deep: true,
      handler () {
        if (!this.canEditSchema) {
          this.restoreParentCustomListFromData()
          this.restoreParentCustomListFromColumns()
          this.restoreColumnRequired()
        }
      }
    }
  },
  created () {
    if (!this.initialRows || !this.initialRows.length) {
      this.initializeDefaultTable()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initializeDataTypeOptions()
      this.attachLayoutObservers()
      this.recalculatePositions()
      if (this.mode === 'TEMPLATE' || this.mode === 'CHECKLIST') {
        this.fetchParentCustomLists()
      }
    })
    window.addEventListener('resize', this.schedulePositionRecalculation)
    window.addEventListener('resize', this.handleDropdownViewportChange)
    window.addEventListener('scroll', this.handleDropdownViewportChange, true)
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy () {
    this.detachLayoutObservers()
    window.removeEventListener('resize', this.schedulePositionRecalculation)
    window.removeEventListener('resize', this.handleDropdownViewportChange)
    window.removeEventListener('scroll', this.handleDropdownViewportChange, true)
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    isConfigurationListType (dataType) {
      return this.normalizeDataType(dataType) === 'CONFIGURATION_LIST'
    },
    getEmptyCellContent (dataType) {
      return this.isConfigurationListType(dataType) ? [] : ''
    },
    getDefaultColumnName (cellIndex) {
      return `Column ${cellIndex + 1}`
    },
    handleHeaderBlur (cell, cellIndex) {
      this.activeHeaderCellId = null
      const trimmedValue = (cell?.content || '').toString().trim()
      if (trimmedValue) {
        if (trimmedValue !== cell.content) {
          cell.content = trimmedValue
          this.onDataChange()
        }
      }
    },
    isFormModeColumnEditable (cellIndex) {
      if (!this.shouldRespectColumnEditability) return true
      if (this.rows.length <= 1) return true
      return this.formColumnDefinitions[cellIndex]?.editable === true
    },
    isAddedRow (rowIndex) {
      return this.addedRowIds.includes(this.rows[rowIndex]?.id)
    },
    canEditLockedColumnCell (rowIndex, cellIndex) {
      if (!this.shouldRespectColumnEditability) return true
      if (this.isFormModeColumnEditable(cellIndex)) return true
      return this.isAddedRow(rowIndex)
    },
    isDataCellDisabled (rowIndex, cellIndex) {
      if (this.editChecklist || this.mode === 'VIEW') return true
      if (this.shouldRespectColumnEditability) return !this.canEditLockedColumnCell(rowIndex, cellIndex)
      return false
    },
    isBooleanCellDisabled (rowIndex, cellIndex) {
      if (!(this.mode === 'CREATE' || this.mode === 'EDIT')) return true
      if (this.editChecklist || this.mode === 'VIEW') return true
      if (this.shouldRespectColumnEditability) return !this.canEditLockedColumnCell(rowIndex, cellIndex)
      return false
    },
    isConfigCellReadOnly (rowIndex, cellIndex) {
      if (this.editChecklist || this.mode === 'VIEW' || this.previewMode) return true
      if (!this.shouldRespectColumnEditability) return false
      if (this.canEditLockedColumnCell(rowIndex, cellIndex)) return false
      return true
    },
    isCellFilled (content, dataType) {
      if (dataType === 'CONFIGURATION_LIST') {
        return Array.isArray(content) && content.length > 0
      }
      if (typeof content === 'boolean' || typeof content === 'number') return true
      if (content === null || content === undefined) return false
      return content.toString().trim() !== ''
    },
    validateFormModeAddedRows (errors) {
      const currentDataRows = this.rows.slice(1)
      const addedRows = currentDataRows.filter(row => this.addedRowIds.includes(row.id))
      if (!addedRows.length) return

      const existingRows = currentDataRows.filter(row => !this.addedRowIds.includes(row.id))

      this.formColumnDefinitions.forEach((column, colIndex) => {
        if (column?.editable === true) return

        const dataType = this.columnDataTypes[colIndex] || column?.type || 'TEXT'
        const hasExistingValue = existingRows.some(row => this.isCellFilled(row.cells[colIndex]?.content, dataType))
        if (!hasExistingValue) return

        const hasAddedRowGap = addedRows.some(row => !this.isCellFilled(row.cells[colIndex]?.content, dataType))
        if (hasAddedRowGap) {
          errors.push(`Either fill all rows or leave all rows empty in ${column?.label || `Column ${colIndex + 1}`}`)
        }
      })
    },
    restoreParentCustomListFromData () {
      if (!this.data || typeof this.data !== 'object') return
      const columns = this.data?.table_data?.columns
      // ← get columnParentCustomList from rows[1] cells first value
      if (!Array.isArray(columns)) return
      columns.forEach((column, index) => {
        if (column?.type === 'CONFIGURATION_LIST' && column?.custom_list_id) {
          this.$set(this.columnParentCustomList, index, {
            id: column.custom_list_id,
            name: column.custom_list_name || ''
          })
        }
      })

      /*
        const firstDataRow = this.rows[1]
        if (!firstDataRow) return
        firstDataRow.cells.forEach((cell, cellIndex) => {
          if (
            Array.isArray(cell.content) &&
        cell.content.length &&
        cell.content[0]?.custom_list_value_id
          ) {
            // first value in content holds the parent list reference
            this.$set(this.columnParentCustomList, cellIndex, {
              id: cell.content[0].custom_list_value_id,
              name: cell.content[0].custom_list_value_name || ''
            })
          }
        })
        return
      }
      const source = this.data?.table_data?.rows || null
      if (!source) return

      Object.keys(source).forEach(colIdx => {
        const entry = source[colIdx]

        if (!entry || typeof entry !== 'object') return

        const customList = Object.values(entry).find(
          value =>
            Array.isArray(value) &&
      value.length &&
      value[0]?.custom_list_id
        )?.[0]

        if (customList) {
          this.$set(this.columnParentCustomList, colIdx, {
            id: customList.custom_list_id,
            name: customList.custom_list_name || ''
          })
        }
      })
      */
    },
    restoreParentCustomListFromColumns () {
      const columns = this.data?.table_data?.columns
      if (!Array.isArray(columns)) return
      columns.forEach((column, index) => {
        if (column?.type === 'CONFIGURATION_LIST' && column?.custom_list_id) {
          this.$set(this.columnParentCustomList, index, {
            id: column.custom_list_id,
            name: column.custom_list_name || ''
          })
        }
      })
    },
    getCustomListIdForCell (rowIndex, cellIndex) {
      return this.getParentCustomListId(cellIndex)
    },
    getParentCustomListId (cellIndex) {
      return this.columnParentCustomList[cellIndex]?.id || null
    },
    getParentCustomList (cellIndex) {
      return this.columnParentCustomList[cellIndex] || null
    },
    getConfigCellKey (rowIndex, cellIndex) {
      return `${rowIndex}-${cellIndex}`
    },
    isConfigCellDropdownOpen (rowIndex, cellIndex) {
      return this.openConfigDropdownKey === this.getConfigCellKey(rowIndex, cellIndex)
    },
    onConfigDropdownStateChange (rowIndex, cellIndex, isOpen) {
      const cellKey = this.getConfigCellKey(rowIndex, cellIndex)
      this.openConfigDropdownKey = isOpen ? cellKey : (this.openConfigDropdownKey === cellKey ? null : this.openConfigDropdownKey)
    },
    restoreCellTagData () {
      const headerRow = this.rows[1]
      if (!headerRow) return
      headerRow.cells.forEach((cell, cellIndex) => {
        if (
          Array.isArray(cell.content) &&
      cell.content.length &&
      cell.content[0]?.custom_list_id
        ) {
          this.$set(this.columnParentCustomList, cellIndex, {
            id: cell.content[0].custom_list_id,
            name: cell.content[0].custom_list_name || ''
          })
        }
      })

      // Restore cellTagData from data rows
      this.rows.forEach((row, rowIndex) => {
        if (rowIndex === 0) return // skip header
        row.cells.forEach((cell, cellIndex) => {
          if (
            Array.isArray(cell.content) &&
        cell.content.length &&
        cell.content[0]?.custom_list_value_id
          ) {
            const key = `${rowIndex}-${cellIndex}`
            this.$set(this.cellTagData, key, {
              tagArray: [
                cell.content.map(v => ({
                  id: v.custom_list_value_id,
                  name: v.custom_list_value_name
                }))
              ]
            })
          }
        })
      })
    },
    getCellTagGroupData (rowIndex, cellIndex) {
      const key = `${rowIndex}-${cellIndex}`
      if (this.mode !== 'CHECKLIST' && this.cellTagData[key]) return this.cellTagData[key]

      const cell = this.rows[rowIndex]?.cells[cellIndex]
      if (Array.isArray(cell?.content) && cell.content.length && cell.content[0]?.custom_list_value_id) {
        return {
          tagArray: [
            cell.content.map(c => ({
              id: c.custom_list_value_id,
              name: c.custom_list_value_name
            }))
          ]
        }
      }

      if (Array.isArray(cell?.content) && cell.content.length) {
        // Skip header row — it has custom_list_id not custom_list_value_id
        if (cell.content[0]?.custom_list_id) return { tagArray: [[]] }

        // Data row — has custom_list_value_id
        if (cell.content[0]?.custom_list_value_id) {
          const source = this.mode === 'CHECKLIST' ? cell.content.slice(1) : cell.content// ← ADD
          const childTags = source.map(c => ({
            id: c.custom_list_value_id,
            name: c.custom_list_value_name
          }))
          return { tagArray: [childTags] }
        }
      }

      return { tagArray: [[]] }
    },
    getHydratedCellTagGroupData (rowIndex, cellIndex) {
      const key = `${rowIndex}-${cellIndex}`
      if (this.mode !== 'CHECKLIST' && this.cellTagData[key]) return this.cellTagData[key]

      const cell = this.rows[rowIndex]?.cells[cellIndex]
      if (Array.isArray(cell?.content) && cell.content.length && cell.content[0]?.custom_list_value_id) {
        return {
          tagArray: [
            cell.content.map(c => ({
              id: c.custom_list_value_id,
              name: c.custom_list_value_name
            }))
          ]
        }
      }

      return this.getCellTagGroupData(rowIndex, cellIndex)
    },

    onConfigTagAdded (rowIndex, cellIndex, { tag, rowIndex: tagRowIndex }) {
      const key = `${rowIndex}-${cellIndex}`
      const current = this.cellTagData[key] || { tagArray: [[]] }
      const currentTags = [...(current.tagArray[0] || [])]

      // Find where to insert — if tag has parentId, it's a child; replace from that level
      const parentIdx = currentTags.findIndex(t => t.id === tag.parentId)
      if (parentIdx !== -1) {
        currentTags.splice(parentIdx + 1) // remove everything after parent
      } else if (tag.parentId === null) {
        currentTags.splice(0) // root level — clear all
      }
      currentTags.push(tag)

      this.$set(this.cellTagData, key, { tagArray: [currentTags] })
      const content = currentTags.map(t => ({ custom_list_value_id: t.id, custom_list_value_name: t.name }))
      // Also update cell.content for save
      const updatedCell = {
        ...this.rows[rowIndex].cells[cellIndex],
        content
      }
      const updatedCells = [...this.rows[rowIndex].cells]
      updatedCells.splice(cellIndex, 1, updatedCell)
      this.$set(this.rows, rowIndex, {
        ...this.rows[rowIndex],
        cells: updatedCells
      })
      this.onDataChange()
    },

    onConfigTagReset (rowIndex, cellIndex) {
      const key = `${rowIndex}-${cellIndex}`
      this.$set(this.cellTagData, key, { tagArray: [[]] })

      const updatedCell = {
        ...this.rows[rowIndex].cells[cellIndex],
        content: []
      }
      const updatedCells = [...this.rows[rowIndex].cells]
      updatedCells.splice(cellIndex, 1, updatedCell)
      this.$set(this.rows, rowIndex, {
        ...this.rows[rowIndex],
        cells: updatedCells
      })
      this.onDataChange()
    },
    filteredParentLists (cellIndex) {
      const search = (this.parentListSearch[cellIndex] || '').toLowerCase().trim()
      if (!search) return this.parentCustomLists
      return this.parentCustomLists.filter(l =>
        l.name.toLowerCase().includes(search)
      )
    },

    selectParentCustomList (cellIndex, item) {
      const previousParentId = this.columnParentCustomList[cellIndex]?.id || null
      this.$set(this.columnParentCustomList, cellIndex, item)
      this.showParentPickerForColumn = null
      if (previousParentId !== item.id) {
        this.resetConfigurationListColumn(cellIndex)
      }
      this.onDataChange()
    },

    async fetchParentCustomLists () {
      try {
        const res = await GetActiveCustomLists()
        this.parentCustomLists = (res?.core_custom_list || []).map(l => ({
          id: l.id,
          name: l.name
        }))
      } catch (e) {
      }
    },

    extractColumnRequired () {
      if (this.initialColumnRequired?.length) {
        return [...this.initialColumnRequired]
      }
      if (this.formColumnDefinitions?.length) {
        return this.formColumnDefinitions.map(col => Boolean(col?.required ?? col?.is_required ?? false))
      }
      if (this.data?.table_data?.columns?.length) {
        return this.data.table_data.columns.map(col => Boolean(col?.required ?? col?.is_required ?? false))
      }
      return Array.from({ length: this.columnCount }, () => false)
    },
    restoreColumnRequired () {
      if (this.canEditSchema && this.columnRequired.length === this.columnCount) {
        return
      }
      if (this.initialColumnRequired?.length) {
        this.columnRequired = [...this.initialColumnRequired]
        return
      }
      if (this.formColumnDefinitions?.length) {
        this.columnRequired = this.formColumnDefinitions.map(col => Boolean(col?.required ?? col?.is_required ?? false))
        return
      }
      if (this.data?.table_data?.columns?.length) {
        this.columnRequired = this.data.table_data.columns.map(col => Boolean(col?.required ?? col?.is_required ?? false))
        return
      }
      if (!this.columnRequired.length || this.columnRequired.length !== this.columnCount) {
        this.columnRequired = Array.from({ length: this.columnCount }, () => false)
      }
    },
    toggleColumnRequired (cellIndex) {
      if (!this.canEditSchema) return
      const current = Boolean(this.columnRequired[cellIndex])
      this.$set(this.columnRequired, cellIndex, !current)
      this.onDataChange()
    },
    initializeDefaultTable () {
      this.rows = [
        {
          id: 'row-1',
          cells: [
            { id: 'cell-1-1', content: '' },
            { id: 'cell-1-2', content: '' },
            { id: 'cell-1-3', content: '' }
          ]
        },
        {
          id: 'row-2',
          cells: [
            { id: 'cell-2-1', content: '' },
            { id: 'cell-2-2', content: '' },
            { id: 'cell-2-3', content: '' }
          ]
        }
      ]
      this.columnCount = 3
      this.columnDataTypes = ['TEXT', 'TEXT', 'TEXT']
      this.columnRequired = [false, false, false]
    },

    normalizeDataType (dataType) {
      return (dataType || '').toString().trim().toUpperCase()
    },
    initializeDataTypeOptions () {
      const supportedTypes = ['TEXT', 'NUMBER', 'DATE', 'BOOLEAN', 'CONFIGURATION_LIST']
      const defaultOptions = [
        { id: 'text', key: 'TEXT', caption: 'Text', icon: iconMap.TEXT },
        { id: 'number', key: 'NUMBER', caption: 'Number', icon: iconMap.NUMBER },
        { id: 'date', key: 'DATE', caption: 'Date', icon: iconMap.DATE },
        { id: 'boolean', key: 'BOOLEAN', caption: 'Boolean', icon: iconMap.BOOLEAN },
        { id: 'configuration list', key: 'CONFIGURATION_LIST', caption: 'Configuration list', icon: iconMap.CONFIGURATION_LIST }
      ]
      const normalizedCustomOptions = (this.customFormFields || [])
        .map(item => {
          const key = this.normalizeDataType(item?.key)
          return { ...item, key, icon: iconMap[key] || iconMap.TEXT }
        })
        .filter(item => supportedTypes.includes(item.key))

      this.dataTypeOptions = normalizedCustomOptions.length ? normalizedCustomOptions : defaultOptions
    },
    schedulePositionRecalculation () {
      if (this.recalculateRafId) cancelAnimationFrame(this.recalculateRafId)
      this.recalculateRafId = requestAnimationFrame(() => {
        this.recalculateRafId = null
        this.updateColumnPositions()
        this.updateRowPositions()
      })
    },
    recalculatePositions () {
      this.schedulePositionRecalculation()
    },
    attachLayoutObservers () {
      if (typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(() => this.schedulePositionRecalculation())
      if (this.$el) this.resizeObserver.observe(this.$el)
      if (this.$refs.mainTable) this.resizeObserver.observe(this.$refs.mainTable)
    },

    detachLayoutObservers () {
      if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null }
      if (this.recalculateRafId) { cancelAnimationFrame(this.recalculateRafId); this.recalculateRafId = null }
    },
    updateColumnPositions () {
      const table = this.$refs.mainTable
      if (!table?.rows?.[0]) return
      const headerRow = table.rows[0]
      const positions = []
      const tableRect = table.getBoundingClientRect()
      for (let i = 0; i < headerRow.cells.length; i++) {
        const rect = headerRow.cells[i]?.getBoundingClientRect()
        if (rect) positions[i] = `${rect.right - tableRect.left}px`
      }
      this.columnPositions = positions
    },

    getColumnPosition (cellIndex) {
      return this.columnPositions[cellIndex] || '0px'
    },

    updateRowPositions () {
      const table = this.$refs.mainTable
      if (!table?.rows) return
      const positions = []
      const tableRect = table.getBoundingClientRect()
      for (let i = 0; i < table.rows.length; i++) {
        const rect = table.rows[i]?.getBoundingClientRect()
        if (rect) positions[i] = `${rect.bottom - tableRect.top}px`
      }
      this.rowPositions = positions
    },

    getRowPosition (rowIndex) {
      return this.rowPositions[rowIndex] || '0px'
    },

    addColumn (afterIndex) {
      this.rows = this.rows.map(row => {
        const newCell = { id: `cell-${row.id}-${Date.now()}`, content: '' }
        if (afterIndex === undefined) {
          return { ...row, cells: [...row.cells, newCell] }
        }
        const newCells = [...row.cells]
        newCells.splice(afterIndex + 1, 0, newCell)
        return { ...row, cells: newCells }
      })
      this.columnCount += 1
      if (afterIndex === undefined) {
        this.columnDataTypes.push('TEXT')
        this.columnRequired.push(false)
      } else {
        this.columnDataTypes.splice(afterIndex + 1, 0, 'TEXT')
        this.columnRequired.splice(afterIndex + 1, 0, false)
      }
      this.$nextTick(() => this.recalculatePositions())
      this.onDataChange()
    },

    addRow (afterIndex) {
      const newRowId = `row-${Date.now()}`
      const newRow = {
        id: newRowId,
        cells: Array.from({ length: this.columnCount }, (_, i) => ({
          id: `cell-${newRowId}-${i + 1}`,
          content: this.getEmptyCellContent(this.columnDataTypes[i])
        }))
      }
      if (afterIndex === undefined) {
        this.rows = [...this.rows, newRow]
      } else {
        const newRows = [...this.rows]
        newRows.splice(afterIndex + 1, 0, newRow)
        this.rows = newRows
      }
      this.addedRowIds.push(newRowId)
      this.$nextTick(() => this.recalculatePositions())
      this.onDataChange()
    },

    deleteColumn (columnIndex) {
      if (this.columnCount <= 1) return

      this.rows = this.rows.map(row => ({
        ...row,
        cells: row.cells.filter((_, i) => i !== columnIndex)
      }))
      this.columnCount -= 1

      this.columnDataTypes.splice(columnIndex, 1)
      this.columnRequired.splice(columnIndex, 1)
      // Clean up cell state for deleted column
      Object.keys(this.cellTagTries).forEach(key => {
        const [, ci] = key.split('-').map(Number)
        if (ci === columnIndex) {
          this.$delete(this.cellTagTries, key)
          this.$delete(this.cellTagGroupData, key)
          this.$delete(this.cellLastParentIds, key)
        }
      })
      this.$nextTick(() => this.recalculatePositions())
      this.onDataChange()
    },

    deleteRow (rowIndex) {
      if (this.rows.length <= 1) return
      const deletedRowId = this.rows[rowIndex]?.id
      this.rows = this.rows.filter((_, i) => i !== rowIndex)
      this.addedRowIds = this.addedRowIds.filter(id => id !== deletedRowId)
      Object.keys(this.cellTagTries).forEach(key => {
        const [ri] = key.split('-').map(Number)
        if (ri === rowIndex) {
          this.$delete(this.cellTagTries, key)
          this.$delete(this.cellTagGroupData, key)
          this.$delete(this.cellLastParentIds, key)
        }
      })
      this.$nextTick(() => this.recalculatePositions())
      this.onDataChange()
    },

    getRefElement (refName) {
      const ref = this.$refs[refName]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (!el) return null
      if (el instanceof HTMLElement) return el
      if (el.$el instanceof HTMLElement) return el.$el
      return null
    },

    updateDataTypeDropdownPosition (cellIndex = this.showDropdownForColumn) {
      if (cellIndex === null || cellIndex === undefined) return
      const btnEl = this.getRefElement(`dataTypeBtn-${cellIndex}`)
      if (!btnEl) return

      const rect = btnEl.getBoundingClientRect()
      const menuWidth = 160
      const menuHeight = 240

      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const noSpaceDown = spaceBelow < menuHeight && spaceAbove > spaceBelow

      let left = rect.right - menuWidth
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8
      }
      if (left < 8) {
        left = 8
      }

      const style = {
        position: 'fixed',
        left: `${left}px`,
        width: `${menuWidth}px`,
        zIndex: 10001
      }

      if (noSpaceDown) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`
        style.top = 'auto'
        style.maxHeight = `${Math.min(300, spaceAbove - 16)}px`
      } else {
        style.top = `${rect.bottom + 4}px`
        style.bottom = 'auto'
        style.maxHeight = `${Math.min(300, spaceBelow - 16)}px`
      }

      this.dataTypeDropdownStyle = style
    },

    updateParentPickerDropdownPosition (cellIndex = this.showParentPickerForColumn) {
      if (cellIndex === null || cellIndex === undefined) return
      const badgeEl = this.getRefElement(`parentPickerBadge-${cellIndex}`) || this.getRefElement(`dataTypeBtn-${cellIndex}`)
      if (!badgeEl) return

      const rect = badgeEl.getBoundingClientRect()
      const menuWidth = Math.max(200, rect.width)
      const menuHeight = 250

      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const noSpaceDown = spaceBelow < menuHeight && spaceAbove > spaceBelow

      let left = rect.left
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8
      }
      if (left < 8) {
        left = 8
      }

      const style = {
        position: 'fixed',
        left: `${left}px`,
        width: `${menuWidth}px`,
        zIndex: 10001
      }

      if (noSpaceDown) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`
        style.top = 'auto'
        style.maxHeight = `${Math.min(300, spaceAbove - 16)}px`
      } else {
        style.top = `${rect.bottom + 4}px`
        style.bottom = 'auto'
        style.maxHeight = `${Math.min(300, spaceBelow - 16)}px`
      }

      this.parentPickerDropdownStyle = style
    },

    handleDropdownViewportChange () {
      if (this.showDropdownForColumn !== null) {
        this.updateDataTypeDropdownPosition(this.showDropdownForColumn)
      }
      if (this.showParentPickerForColumn !== null) {
        this.updateParentPickerDropdownPosition(this.showParentPickerForColumn)
      }
    },

    toggleDataTypeDropdown (cellIndex) {
      if (this.showDropdownForColumn === cellIndex) {
        this.closeDropdown()
      } else {
        this.showDropdownForColumn = cellIndex
        this.showParentPickerForColumn = null
        this.$nextTick(() => {
          this.updateDataTypeDropdownPosition(cellIndex)
        })
      }
    },

    selectDataType (cellIndex, dataType) {
      const normalized = this.normalizeDataType(dataType)
      const current = this.normalizeDataType(this.columnDataTypes[cellIndex])
      if (current !== normalized && this.hasColumnData(cellIndex)) {
        const msg = `Changing from "${this.getDataTypeLabel(current)}" to "${this.getDataTypeLabel(normalized)}" will clear all data in this column. Continue?`
        if (confirm(msg)) {
          this.clearColumnData(cellIndex)
          this.columnDataTypes[cellIndex] = normalized
          this.onDataChange()
        }
      } else {
        this.columnDataTypes[cellIndex] = normalized
        this.onDataChange()
      }
      if (normalized !== 'CONFIGURATION_LIST') {
        this.clearConfigurationListState(cellIndex)
        this.closeDropdown()
      } else {
        this.resetConfigurationListColumn(cellIndex)
        this.showDropdownForColumn = null
        if (this.mode === 'TEMPLATE' || this.mode === 'CHECKLIST') {
          this.openParentPicker(cellIndex)
        } else {
          this.closeDropdown()
        }
      }
    },

    openParentPicker (cellIndex) {
      if (!this.canEditSchema) return
      if (this.showParentPickerForColumn === cellIndex) {
        this.closeDropdown()
      } else {
        this.showDropdownForColumn = null
        this.showParentPickerForColumn = cellIndex
        this.$nextTick(() => {
          this.updateParentPickerDropdownPosition(cellIndex)
        })
      }
    },

    getDataTypeLabel (dataType) {
      const normalized = this.normalizeDataType(dataType)
      const option = this.dataTypeOptions.find(opt => this.normalizeDataType(opt.key) === normalized)
      return option ? (option.caption || option.label) : 'Text'
    },

    getDataTypeIcon (dataType) {
      return iconMap[this.normalizeDataType(dataType)] || iconMap.TEXT
    },

    hasColumnData (columnIndex) {
      for (let i = 1; i < this.rows.length; i++) {
        const content = this.rows[i].cells[columnIndex]?.content
        if (Array.isArray(content)) {
          if (content.length > 0) return true
          continue
        }
        if (typeof content === 'boolean') return true
        if (content !== null && content !== undefined && content.toString().trim() !== '') return true
      }
      return false
    },

    clearColumnData (columnIndex) {
      const emptyContent = this.getEmptyCellContent(this.columnDataTypes[columnIndex])
      for (let i = 1; i < this.rows.length; i++) {
        const cell = this.rows[i].cells[columnIndex]
        if (cell) {
          cell.content = Array.isArray(emptyContent) ? [] : emptyContent
          cell.valueId = null
          cell.customListId = null
        }
      }
      // Also clear trie state for all cells in this column
      Object.keys(this.cellTagTries).forEach(key => {
        const [, ci] = key.split('-').map(Number)
        if (ci === columnIndex) {
          this.cellTagTries[key].clearAll()
          this.$set(this.cellTagGroupData, key, this.cellTagTries[key].groupTagInArray())
          this.$set(this.cellLastParentIds, key, null)
        }
      })
    },

    resetConfigurationListColumn (columnIndex) {
      for (let rowIndex = 1; rowIndex < this.rows.length; rowIndex++) {
        const updatedCell = {
          ...this.rows[rowIndex].cells[columnIndex],
          content: []
        }
        const updatedCells = [...this.rows[rowIndex].cells]
        updatedCells.splice(columnIndex, 1, updatedCell)
        this.$set(this.rows, rowIndex, {
          ...this.rows[rowIndex],
          cells: updatedCells
        })
      }

      Object.keys(this.cellTagData).forEach(key => {
        const [, ci] = key.split('-').map(Number)
        if (ci === columnIndex) {
          this.$set(this.cellTagData, key, { tagArray: [[]] })
        }
      })
    },

    clearConfigurationListState (columnIndex) {
      this.$delete(this.columnParentCustomList, columnIndex)
      Object.keys(this.cellTagData).forEach(key => {
        const [, ci] = key.split('-').map(Number)
        if (ci === columnIndex) {
          this.$delete(this.cellTagData, key)
        }
      })
    },

    closeDropdown () {
      this.showDropdownForColumn = null
      this.showParentPickerForColumn = null
      this.dataTypeDropdownStyle = {}
      this.parentPickerDropdownStyle = {}
    },

    handleOutsideClick (event) {
      if (!this.$el || !this.$el.contains(event.target)) {
        this.closeDropdown()
        return
      }
      const isDropdown = event.target.closest('.dropdown-menu')
      const isButton = event.target.closest('.data-type-btn')
      const isBadge = event.target.closest('.parent-list-badge')

      if (!isDropdown && !isButton && !isBadge) {
        this.closeDropdown()
      }
    },

    async validateTable (options = {}) {
      const shouldCheckRequired = options.checkRequired !== undefined ? options.checkRequired : !options.skipRequiredCheck
      const errors = []

      const headerRow = this.rows[0]
      if (headerRow) {
        headerRow.cells.forEach((cell, index) => {
          if (!cell.content || !cell.content.trim()) {
            errors.push(`Column ${index + 1} header is empty`)
          }
        })

        if (!this.isFormMode) {
          for (let colIndex = 0; colIndex < this.columnCount; colIndex++) {
            const columnCells = this.rows.slice(1).map(row => row.cells[colIndex]?.content || '')
            const hasAnyContent = columnCells.some(c => typeof c === 'boolean' || (c && c.toString().trim() !== ''))
            const allFilled = columnCells.every(c => typeof c === 'boolean' || (c && c.toString().trim() !== ''))
            if (hasAnyContent && !allFilled) {
              const columnName = headerRow.cells[colIndex]?.content || `Column ${colIndex + 1}`
              errors.push(`"${columnName}": Either fill all rows or leave all rows empty.`)
            }
          }
        } else {
          this.validateFormModeAddedRows(errors)
        }

        if (shouldCheckRequired && (this.isFormMode || this.mode === 'CREATE' || this.mode === 'EDIT')) {
          const dataRows = this.rows.slice(1)
          const mandatoryColumns = []
          for (let colIndex = 0; colIndex < this.columnCount; colIndex++) {
            if (this.columnRequired[colIndex]) {
              const columnName = headerRow.cells[colIndex]?.content || `Column ${colIndex + 1}`
              mandatoryColumns.push(columnName)
              if (dataRows.length > 0) {
                const hasEmptyCell = dataRows.some(row => !this.isCellFilled(row.cells[colIndex]?.content, this.columnDataTypes[colIndex]))
                if (hasEmptyCell) {
                  errors.push(`"${columnName}" is a mandatory column. Please fill in all rows.`)
                }
              }
            }
          }
          if (mandatoryColumns.length > 0 && dataRows.length === 0) {
            const tableLabel = this.data?.caption || this.data?.field_name || 'Table'
            const columnText = mandatoryColumns.length === 1
              ? `mandatory column "${mandatoryColumns[0]}"`
              : `mandatory columns (${mandatoryColumns.map(c => `"${c}"`).join(', ')})`
            errors.push(`"${tableLabel}" must contain at least one row because it has ${columnText}.`)
          }
        }
      }
      const configInputRefs = Object.keys(this.$refs || {})
        .filter(refKey => refKey.startsWith('configInput-'))
      for (const refKey of configInputRefs) {
        const refValue = this.$refs[refKey]
        const component = Array.isArray(refValue) ? refValue[0] : refValue
        if (component?.validateSelection) {
          const isSelectionValid = await component.validateSelection()
          if (!isSelectionValid) {
            errors.push('Please select a leaf item for all Configuration List cells.')
            break
          }
        }
      }
      return { isValid: errors.length === 0, errors }
    },

    getTableData () {
      return {
        rows: this.rows,
        columnDataTypes: this.columnDataTypes,
        columnParentCustomList: this.columnParentCustomList,
        columnRequired: this.columnRequired
      }
    },

    onDataChange () {
      this.$emit('table-data-updated', {
        rows: this.rows,
        columnDataTypes: this.columnDataTypes,
        columnParentCustomList: this.columnParentCustomList,
        columnRequired: this.columnRequired
      })
    }
  }
}

</script>
<style lang="scss" scoped>
.checkbox-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.cell-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}
.advanced-table-creator {
  background: white;
}

.table-container {
  padding: 15px 0 5px 0;
  overflow: auto;
}

.table-wrapper {
  position: relative;
  overflow: visible;
}

.table-positioning {
  position: relative;
  margin-top: 1rem;
  margin-left: 2rem;
}

.column-indicators {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-1.25rem);
  overflow: visible;
  padding: 10px 0;

  .column-indicator-wrapper {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    height: 2rem;
    width: 2rem;

    .column-insert-indicator {
      position: relative;
      cursor: pointer;

      .indicator-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #d1d5db;
        transition: opacity 0.2s;
        margin: 0 auto;
        display: block;
        box-sizing: border-box;
        border: none;
        position: relative;
        flex-shrink: 0;
      }

      .insert-btn {
        position: absolute;
        top: 30%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 20px;
        height: 20px;
        border: none;
        border-radius: 3px;
        background: #374151;
        color: white;
        font-size: var(--text-s);
        font-weight: bold;
        cursor: pointer;
        opacity: 0;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        z-index: 40;

        &:hover {
          background: #1f2937;
          transform: translate(-50%, -50%) scale(1.1);
        }
      }

      &:hover {
        .indicator-dot {
          opacity: 0;
        }

        .insert-btn {
          opacity: 1;
        }
      }
    }
  }
}

.row-indicators {
  position: absolute;
  left: 0;
  top: 0;
  transform: translateX(-2.25rem);
  overflow: visible;
  padding: 10px 0;

  .row-indicator-wrapper {
    position: absolute;
    left: 0;
    transform: translateY(0px);
    height: 2rem;
    width: 2rem;

    .row-insert-indicator {
      position: relative;
      cursor: pointer;

      .indicator-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #d1d5db;
        transition: opacity 0.2s;
        margin: 0 auto;
        display: block;
        box-sizing: border-box;
        border: none;
        position: relative;
        flex-shrink: 0;
      }

      .insert-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 20px;
        height: 20px;
        border: none;
        border-radius: 3px;
        background: #374151;
        color: white;
        font-size: var(--text-s);
        font-weight: bold;
        cursor: pointer;
        opacity: 0;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        &:hover {
          background: #1f2937;
          transform: translate(-50%, -50%) scale(1.1);
        }
      }

      &:hover {
        .indicator-dot {
          opacity: 0;
        }

        .insert-btn {
          opacity: 1;
        }
      }
    }
  }
}

.main-table {
  width: max-content;
  min-width: 98%;
  border-collapse: collapse;
  border: 1px solid #e5e7eb;

  .table-row {
    border-bottom: 1px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }

    &.header-row {
      border-bottom: 2px solid #d1d5db;
      background: #f9fafb;

      &:hover {
        background: #f9fafb;
      }

      .table-cell {
        border-bottom: 2px solid #d1d5db;
        background: #f9fafb;
      }

      .row-delete-cell {
        border-bottom: 2px solid #d1d5db;
        background: #f9fafb;
      }

      .cell-input {
        font-weight: 500;
        background: #f9fafb;
      }
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .table-cell {
    min-width: 140px;
    border-right: 1px solid #e5e7eb;
    vertical-align: middle;
    box-sizing: border-box;

    &:last-child {
      border-right: none;
    }

    .cell-content {
      position: relative;
      background: white;
      min-height: 40px;
      height: 40px;
      width: 100%;
      box-sizing: border-box;
      display: flex;
      align-items: center;

      &.header-cell {
        background: #f9fafb;
        // border-bottom: 2px solid #d1d5db;
        display: flex;
        align-items: center;
        padding: 0 0.5rem;
        gap: 0.35rem;
        min-height: 40px;
        height: 40px;
        width: 100%;
        box-sizing: border-box;
      }

      .data-type-icon-wrapper {
        width: 16px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        .data-type-icon {
          pointer-events: none;
          opacity: 0.75;
          object-fit: contain;
          display: block;
        }

        &.type-text .data-type-icon {
          width: 10px;
          height: 11px;
        }

        &.type-number .data-type-icon {
          width: 9px;
          height: 12px;
        }

        &.type-boolean .data-type-icon {
          width: 15px;
          height: 11px;
        }

        &.type-date .data-type-icon {
          width: 12px;
          height: 12px;
        }

        &.type-time .data-type-icon {
          width: 12px;
          height: 12px;
        }

        &.type-long_text .data-type-icon {
          width: 11px;
          height: 11px;
        }

        &.type-configuration_list .data-type-icon {
          width: 13px;
          height: 11px;
        }
      }

      .cell-input {
        width: 100%;
        border: none;
        padding: 0.5rem 0.75rem;
        background: transparent;
        outline: none;
        border-radius: 0;
        min-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        font-size: var(--text-base);
        box-sizing: border-box;
        margin: 0;

        &:focus {
          background: #f0f9ff;
          box-shadow: inset 0 0 0 1px #3b82f6;
        }

        &.header-input {
          flex: 1 1 0;
          min-width: 30px;
          height: 30px;
          min-height: 30px;
          padding: 0 0.25rem;
          font-weight: 500;
          font-size: var(--text-base);
          color: #374151;
          background: transparent;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          box-sizing: border-box;
          margin: 0;

          &:focus {
            background: #f0f9ff;
            box-shadow: inset 0 0 0 1px #3b82f6;
            border-radius: 3px;
          }

          &:disabled {
            color: #374151;
            cursor: default;
          }
        }
      }

      .header-column-label {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        flex: 1 1 0;
        min-width: 30px;
        height: 30px;
        min-height: 30px;
        padding: 0 0.25rem;
        overflow: hidden;
        box-sizing: border-box;

        .header-label-text {
          font-weight: 500;
          font-size: var(--text-base);
          color: #374151;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 0 1 auto;
        }

        .column-required-asterisk {
          font-size: 15px;
          font-weight: 700;
          color: #dc2626;
          line-height: 1;
          flex-shrink: 0;
          user-select: none;
          margin-left: 0;
        }
      }

      .checkbox-wrapper {
        width: 100%;
        height: 100%;
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
      }

      .header-required-toggle {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
        flex-shrink: 0;
        padding: 2px;
        border-radius: 9999px;
        transition: opacity 0.15s ease, transform 0.15s ease;

        &:hover {
          opacity: 0.9;
          transform: scale(1.05);
        }

        .header-toggle-track {
          width: 24px;
          height: 14px;
          background: #d1d5db;
          border-radius: 9999px;
          position: relative;
          transition: background-color 0.2s ease, box-shadow 0.2s ease;

          .header-toggle-thumb {
            position: absolute;
            top: 2px;
            left: 2px;
            width: 10px;
            height: 10px;
            background: #ffffff;
            border-radius: 50%;
            transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
          }
        }

        &.is-checked {
          .header-toggle-track {
            background: var(--brand-color-1, #DFA600);
            box-shadow: 0 0 4px rgba(223, 166, 0, 0.4);

            .header-toggle-thumb {
              transform: translateX(10px);
            }
          }
        }
      }

      .column-required-asterisk {
        font-size: 15px;
        font-weight: 700;
        color: #dc2626;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 2px;
        user-select: none;
        flex-shrink: 0;
      }

      .parent-list-badge {
        flex: 0 1 auto;
        max-width: 95px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        font-size: 11px;
        font-weight: 500;
        line-height: 1.2;
        color: var(--brand-color-1, #2563eb);
        background-color: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 4px;
        white-space: nowrap;
        overflow: hidden;
        box-sizing: border-box;
        flex-shrink: 1;

        .badge-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1 1 auto;
        }

        .badge-chevron {
          width: 7px;
          height: 4px;
          flex-shrink: 0;
          opacity: 0.7;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }

        &.is-placeholder {
          color: #4b5563;
          background-color: #f3f4f6;
          border-color: #d1d5db;
          border-style: dashed;
        }

        &.is-clickable {
          cursor: pointer;
          user-select: none;
          transition: all 0.15s ease;

          &:hover {
            background-color: #dbeafe;
            border-color: #93c5fd;
            box-shadow: 0 1px 2px rgba(37, 99, 235, 0.12);

            .badge-chevron {
              opacity: 1;
              transform: translateY(0.5px);
            }
          }

          &.is-placeholder:hover {
            background-color: #e5e7eb;
            color: #374151;
            border-color: #9ca3af;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          }
        }
      }

      .delete-btn {
        position: absolute;
        top: 0.25rem;
        right: 0.25rem;
        width: 20px;
        height: 20px;
        border: none;
        background: transparent;
        cursor: pointer;
        opacity: 0;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-s);
        border-radius: 3px;

        .delete-icon {
          font-size: var(--text-base);
          color: #dc2626;
        }

        &:hover {
          background: #fef2f2;

          .delete-icon {
            color: #b91c1c;
          }
        }
      }
    }

    &:hover .delete-btn {
      opacity: 1;
    }
  }

  .row-delete-cell {
    width: 2rem;
    padding: 0.25rem;
    text-align: center;
    vertical-align: middle;
    box-sizing: border-box;

    .row-delete-btn {
      opacity: 1;
      transition: all 0.2s;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      margin: 0 auto;
      box-sizing: border-box;
      img {
        width: 14px;
        height: 14px;
      }

      &:hover {
        background: #fef2f2;
      }
    }
  }

  .add-row-row {
    .add-row-cell {
      padding: 0;
      border-top: 1px solid #e5e7eb;

      .add-row-end-btn {
        width: 100%;
        padding: 0.5rem;
        border: none;
        background: transparent;
        color: #6b7280;
        font-size: var(--text-base);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: all 0.2s;

        &:hover {
          background: #f9fafb;
          color: #374151;
        }

        .icon {
          font-size: var(--text-l);
        }
      }
    }
  }
}

.config-list-cell {
  position: relative;
  z-index: 1;
  overflow: visible;
  width: 100%;
  height: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0;
  margin: 0;

  &.is-dropdown-open {
    z-index: 3000;
  }

  ::v-deep .selector-wrap {
    width: 100%;
    height: 100%;
    min-height: 40px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  ::v-deep .selector-row {
    width: 100%;
    height: 100%;
    min-height: 40px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  ::v-deep .selector-bar {
    width: 100%;
    min-height: 40px;
    height: 40px;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
    padding: 0 0.5rem;
    box-sizing: border-box;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    overflow-x: auto;
    overflow-y: hidden;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  ::v-deep .crumb {
    padding: 3px 6px;
    font-size: var(--text-s, 12px);
    border-radius: 4px;
    line-height: 1.2;
    flex-shrink: 0;
  }

  ::v-deep .view-crumb {
    padding: 3px 6px;
    font-size: var(--text-s, 12px);
    line-height: 1.2;
    flex-shrink: 0;
  }

  ::v-deep .crumb-separator {
    font-size: 11px;
    flex-shrink: 0;
  }

  ::v-deep .add-btn {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    font-size: 14px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin: 0;
  }
}

.table-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;

  .table-stats {
    font-size: var(--text-s);
    color: #6b7280;
  }
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;

  &.btn-primary {
    background: #3b82f6;
    border: 1px solid #3b82f6;
    color: white;

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  }
}

/* Data Type Dropdown Styles */
.data-type-dropdown {
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.data-type-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  padding: 0;
  margin: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  box-sizing: border-box;
  line-height: 1;

  &:hover {
    border-color: #9ca3af;
    background: #f3f4f6;
    color: #374151;
  }

  .dropdown-triangle {
    display: block;
    width: 8px;
    height: 5px;
    pointer-events: none;
    flex-shrink: 0;
  }
}

.dropdown-menu {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border: 1px solid #e5e7eb;
  z-index: 10001;
  max-height: 300px;
  overflow-y: auto;
  min-width: 150px;
  padding: 8px;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
}

.dropdown-search {
  margin-bottom: 6px;

  .dropdown-search-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: var(--text-s, 12px);
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: #3b82f6;
    }
  }
}

.dropdown-item {
  padding: 6px 8px;
  cursor: pointer;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  font-size: var(--text-base);

  &:hover {
    background: #f3f4f6;
  }

  &.selected {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 500;
  }

  &.required-toggle-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .required-asterisk-icon {
      font-size: 16px;
      font-weight: 700;
      color: #dc2626;
      line-height: 1;
      width: 14px;
      text-align: center;
      flex-shrink: 0;
    }

    .mini-toggle {
      width: 28px;
      height: 16px;
      background: #e5e7eb;
      border-radius: 9999px;
      position: relative;
      transition: background-color 0.2s ease;
      flex-shrink: 0;

      .mini-toggle-slider {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 12px;
        height: 12px;
        background: white;
        border-radius: 50%;
        transition: transform 0.2s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }

      &.is-checked {
        background: var(--brand-color-1, #DFA600);
        box-shadow: 0 0 4px rgba(223, 166, 0, 0.35);

        .mini-toggle-slider {
          transform: translateX(12px);
        }
      }
    }

    &.is-active {
      background: rgba(223, 166, 0, 0.1);
      color: #854d0e;
      font-weight: 500;
    }
  }

  &.delete-option {
    color: #dc2626;
    &:hover {
      background: #fef2f2;
    }
  }

  .option-icon-wrapper {
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .option-icon {
      object-fit: contain;
      display: block;
    }

    &.type-text .option-icon {
      width: 10px;
      height: 11px;
    }

    &.type-number .option-icon {
      width: 9px;
      height: 12px;
    }

    &.type-boolean .option-icon {
      width: 15px;
      height: 11px;
    }

    &.type-date .option-icon {
      width: 12px;
      height: 12px;
    }

    &.type-time .option-icon {
      width: 12px;
      height: 12px;
    }

    &.type-long_text .option-icon {
      width: 11px;
      height: 11px;
    }

    &.type-configuration_list .option-icon {
      width: 13px;
      height: 11px;
    }

    &.type-delete .option-icon {
      width: 13px;
      height: 13px;
    }
  }

  .option-label {
    flex: 1;
  }
}

.dropdown-partition {
  height: 1px;
  background: #e5e7eb;
  margin: 6px 4px;
}
</style>
